import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import VeeqoSyncModal from '../Shared/VeeqoSyncModal';

// API Templates Configuration
const API_TEMPLATES = {
  'amazon-sp-api': {
    name: 'Amazon SP-API',
    type: 'e-commerce',
    icon: '📦',
    endpoint: 'https://sellingpartnerapi-na.amazon.com',
    fields: [
      { name: 'environment', label: 'Environment', type: 'select', required: true, options: ['production', 'sandbox'] },
      { name: 'region', label: 'Region', type: 'select', required: true, options: ['na', 'eu', 'fe'] },
      { name: 'client_id', label: 'Client ID', type: 'text', required: true },
      { name: 'client_secret', label: 'Client Secret', type: 'password', required: true },
      { name: 'refresh_token', label: 'Refresh Token', type: 'password', required: true },
      { name: 'seller_id', label: 'Seller ID (Merchant ID)', type: 'text', required: false },
      { name: 'marketplace_id', label: 'Marketplace ID', type: 'text', required: true, hint: 'US: ATVPDKIKX0DER | CA: A2EUQ1WTGCTBG2' }
    ],
    configuration: {
      auto_sync: true,
      sync_frequency: 'daily',
      sync_time: '06:00',
      enabled_features: ['settlement_reports', 'orders', 'finances']
    }
  },
  'sendgrid': {
    name: 'SendGrid Email',
    type: 'email',
    icon: '📧',
    endpoint: 'https://api.sendgrid.com',
    fields: [
      { name: 'api_key', label: 'API Key', type: 'password', required: true }
    ]
  },
  'google-places': {
    name: 'Google Places API',
    type: 'location',
    icon: '📍',
    endpoint: 'https://maps.googleapis.com',
    fields: [
      { name: 'api_key', label: 'API Key', type: 'password', required: true }
    ]
  },
  'razorpay': {
    name: 'Razorpay',
    type: 'payment',
    icon: '💳',
    endpoint: 'https://api.razorpay.com',
    fields: [
      { name: 'key_id', label: 'Key ID', type: 'text', required: true },
      { name: 'key_secret', label: 'Key Secret', type: 'password', required: true }
    ]
  },
  'veeqo': {
    name: 'Veeqo',
    type: 'order-enrichment',
    icon: '🚀',
    endpoint: 'https://api.veeqo.com',
    description: 'Enrich settlement data with order details from Veeqo',
    fields: [
      { name: 'api_key', label: 'Veeqo API Key', type: 'password', required: true, placeholder: 'Enter your Veeqo API Key' }
    ],
    configuration: {
      auto_sync: false,
      enabled_features: ['order_details', 'inventory_sync']
    }
  },
  'keepa': {
    name: 'Keepa',
    type: 'market-intelligence',
    icon: '📊',
    endpoint: 'https://api.keepa.com',
    description: 'Historical price & BSR data for competitive analysis',
    fields: [
      { name: 'api_key', label: 'Keepa API Key', type: 'password', required: true, placeholder: 'Enter your Keepa API Key' }
    ],
    configuration: {
      cache_ttl_hours: 24,
      enabled_features: ['price_history', 'bsr_history', 'offers']
    }
  }
};

const APIConfiguration = () => {
  const { tenantId } = useAuth();
  const [apiConfigs, setApiConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [formData, setFormData] = useState({});
  const [editingConfig, setEditingConfig] = useState(null);
  const [testingConnection, setTestingConnection] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [selectedEnvironment, setSelectedEnvironment] = useState('production');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successDetails, setSuccessDetails] = useState(null);
  
  // Veeqo Sync States
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);

  useEffect(() => {
    fetchAPIConfigurations();
    fetchSyncStatus(); // Fetch Veeqo sync status on load
  }, [tenantId]);

  // Fetch Veeqo sync status
  const fetchSyncStatus = async () => {
    try {
      const data = await api.get('/api/v1/veeqo/sync-status', {
        headers: { 'X-Tenant-ID': tenantId || 'tenant-123' }
      });
      setSyncStatus(data);
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
    }
  };

  // Handle opening sync modal
  const handleOpenSyncModal = () => {
    fetchSyncStatus(); // Refresh status before showing modal
    setShowSyncModal(true);
  };

  // Handle starting a sync
  const handleStartSync = async (syncType) => {
    try {
      setSyncing(true);
      setSyncProgress({ type: syncType, message: 'Starting sync...' });
      
      let params = new URLSearchParams();
      
      if (syncType === 'last30') {
        const endDate = new Date().toISOString().split('T')[0];
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        params.append('start_date', startDate.toISOString().split('T')[0]);
        params.append('end_date', endDate);
        params.append('max_pages', '10');
      } else if (syncType === 'year2025') {
        params.append('start_date', '2025-01-01');
        params.append('end_date', '2025-12-31');
        params.append('max_pages', '50');
      } else if (syncType === 'full') {
        params.append('max_pages', '100');
      }
      
      // Start sync (this runs in background for large syncs)
      const response = await api.post(`/api/v1/veeqo/sync-all?${params.toString()}`, {}, {
        headers: { 'X-Tenant-ID': tenantId || 'tenant-123' }
      });
      
      // Update status
      setSyncProgress({ 
        type: syncType, 
        message: `✓ Sync complete! ${response.total_synced || 0} orders synced.`,
        complete: true,
        stats: response
      });
      
      // Refresh status
      await fetchSyncStatus();
      
      // Auto-close after showing success
      setTimeout(() => {
        setShowSyncModal(false);
        setSyncing(false);
        setSyncProgress(null);
      }, 3000);
      
    } catch (error) {
      console.error('Sync failed:', error);
      setSyncProgress({ 
        type: syncType, 
        message: `❌ Sync failed: ${error.message}`,
        error: true
      });
      setSyncing(false);
    }
  };

  // Helper function to build composite key for APIs with environment support
  const buildCompositeKey = (templateKey, environment) => {
    const template = API_TEMPLATES[templateKey];
    const hasEnvironment = template?.fields?.some(field => field.name === 'environment');
    
    if (hasEnvironment && environment) {
      return `${templateKey}-${environment}`;
    }
    return templateKey;
  };

  // Helper function to extract base template from composite key
  const extractBaseTemplate = (compositeKey) => {
    // Check if this is a composite key (e.g., "amazon-sp-api-sandbox")
    for (const templateKey in API_TEMPLATES) {
      if (compositeKey.startsWith(templateKey + '-')) {
        return templateKey;
      }
    }
    return compositeKey;
  };

  // Helper function to extract environment from composite key
  const extractEnvironment = (compositeKey) => {
    const parts = compositeKey.split('-');
    const lastPart = parts[parts.length - 1];
    if (lastPart === 'sandbox' || lastPart === 'production') {
      return lastPart;
    }
    return null;
  };

  // Helper function to get display name with environment
  const getDisplayName = (config) => {
    const baseTemplate = extractBaseTemplate(config.api_name);
    const environment = extractEnvironment(config.api_name);
    const template = API_TEMPLATES[baseTemplate];
    
    if (environment && template) {
      return `${template.name} (${environment.charAt(0).toUpperCase() + environment.slice(1)})`;
    }
    return template?.name || config.api_name;
  };

  const fetchAPIConfigurations = async () => {
    try {
      setLoading(true);
      // API client returns data directly, not response.data
      const data = await api.get('/api/v1/settings/integrations', {
        headers: {
          'X-Tenant-ID': tenantId || 'tenant-123'
        }
      });
      setApiConfigs(data || []);
    } catch (error) {
      console.error('Error fetching API configurations:', error);
      setApiConfigs([]); // Set empty array on error to prevent undefined
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = (templateKey) => {
    setSelectedTemplate(templateKey);
    setEditingConfig(null);
    
    // Initialize form data with default values
    const template = API_TEMPLATES[templateKey];
    const initialData = {};
    template.fields.forEach(field => {
      if (field.type === 'select' && field.options && field.options.length > 0) {
        initialData[field.name] = field.options[0];
      } else {
        initialData[field.name] = '';
      }
    });
    
    // Set initial environment for APIs that have environment field
    const hasEnvironment = template.fields.some(field => field.name === 'environment');
    if (hasEnvironment) {
      setSelectedEnvironment(initialData.environment || 'production');
    }
    
    setFormData(initialData);
    setShowModal(true);
    setTestResult(null);
  };

  const openEditModal = async (apiName) => {
    try {
      const data = await api.get(`/api/v1/settings/integrations/${apiName}`, {
        headers: {
          'X-Tenant-ID': tenantId || 'tenant-123'
        }
      });
      
      setEditingConfig(data);
      
      // Extract base template and environment from composite key
      const baseTemplate = extractBaseTemplate(apiName);
      const environment = extractEnvironment(apiName);
      
      setSelectedTemplate(baseTemplate);
      if (environment) {
        setSelectedEnvironment(environment);
      }
      
      setFormData(data.credentials || {});
      setShowModal(true);
      setTestResult(null);
    } catch (error) {
      console.error('Error fetching API configuration:', error);
      toast.error('❌ Failed to load configuration');
    }
  };

  // Handle environment change - fetch config for selected environment
  const handleEnvironmentChange = async (newEnvironment) => {
    setSelectedEnvironment(newEnvironment);
    
    // Update form data environment field
    setFormData({ ...formData, environment: newEnvironment });
    
    // Build composite key for this environment
    const compositeKey = buildCompositeKey(selectedTemplate, newEnvironment);
    
    // Try to fetch existing config for this environment
    try {
      const data = await api.get(`/api/v1/settings/integrations/${compositeKey}`, {
        headers: {
          'X-Tenant-ID': tenantId || 'tenant-123'
        }
      });
      
      // Config exists for this environment, populate the form
      setFormData(data.credentials || {});
      setEditingConfig(data);
      console.log(`✅ Loaded existing config for ${compositeKey}`);
    } catch (error) {
      // No config exists for this environment
      
      // Special handling for sandbox environment - auto-fill defaults
      if (newEnvironment === 'sandbox' && selectedTemplate === 'amazon-sp-api') {
        try {
          const sandboxDefaults = await api.get('/api/v1/settings/integrations/sandbox-defaults', {
            headers: {
              'X-Tenant-ID': tenantId || 'tenant-123'
            }
          });
          
          setFormData(sandboxDefaults);
          setEditingConfig(null);
          console.log('✅ Auto-filled sandbox defaults (refresh token empty - user must provide)');
        } catch (sandboxError) {
          console.warn('⚠️ Could not fetch sandbox defaults, using empty form');
          // Fallback to empty form
          const template = API_TEMPLATES[selectedTemplate];
          const emptyData = { environment: newEnvironment };
          template.fields.forEach(field => {
            if (field.name !== 'environment') {
              if (field.type === 'select' && field.options && field.options.length > 0) {
                emptyData[field.name] = field.options[0];
              } else {
                emptyData[field.name] = '';
              }
            }
          });
          setFormData(emptyData);
          setEditingConfig(null);
        }
      } else {
        // For production or other environments, clear the form with defaults
        const template = API_TEMPLATES[selectedTemplate];
        const emptyData = { environment: newEnvironment };
        template.fields.forEach(field => {
          if (field.name !== 'environment') {
            // For select fields, use first option as default
            if (field.type === 'select' && field.options && field.options.length > 0) {
              emptyData[field.name] = field.options[0];
            } else {
              emptyData[field.name] = '';
            }
          }
        });
        setFormData(emptyData);
        setEditingConfig(null);
        console.log(`ℹ️ No existing config for ${compositeKey}, form cleared with defaults`);
      }
    }
  };

  const handleSaveConfiguration = async () => {
    try {
      const template = API_TEMPLATES[selectedTemplate];
      
      // Validate required fields
      const missingFields = template.fields
        .filter(field => field.required && !formData[field.name])
        .map(field => field.label);
      
      if (missingFields.length > 0) {
        toast.warning(`⚠️ Please fill in required fields: ${missingFields.join(', ')}`);
        return;
      }

      // Build composite key based on environment (if applicable)
      const environment = formData.environment;
      const compositeKey = buildCompositeKey(selectedTemplate, environment);

      const payload = {
        api_name: compositeKey, // Use composite key
        api_type: template.type,
        credentials: {
          ...formData,
          endpoint: template.endpoint
        },
        configuration: template.configuration || {}
      };

      await api.post(
        `/api/v1/settings/integrations/${compositeKey}`,
        payload,
        {
          headers: {
            'X-Tenant-ID': tenantId || 'tenant-123'
          }
        }
      );

      // Show success modal with details
      setSuccessDetails({
        apiName: template.name,
        environment: environment || 'default',
        timestamp: new Date().toLocaleString(),
        icon: template.icon,
        sellerId: formData.seller_id || 'Not provided',
        region: formData.region || 'Not specified',
        marketplace: formData.marketplace_id || 'Not specified'
      });
      setShowSuccessModal(true);
      setShowModal(false);
      
      // Auto-close after 3 seconds
      setTimeout(() => setShowSuccessModal(false), 3000);
      
      fetchAPIConfigurations();
    } catch (error) {
      console.error('Error saving API configuration:', error);
      toast.error('❌ Failed to save configuration: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleTestConnection = async (apiName) => {
    try {
      setTestingConnection(apiName);
      setTestResult(null);
      
      const data = await api.post(
        `/api/v1/settings/integrations/${apiName}/test`,
        {},
        {
          headers: {
            'X-Tenant-ID': tenantId || 'tenant-123'
          }
        }
      );

      setTestResult(data);
    } catch (error) {
      console.error('Error testing connection:', error);
      setTestResult({
        status: 'error',
        message: error.response?.data?.detail || error.message
      });
    } finally {
      setTestingConnection(null);
    }
  };

  const handleDeleteConfiguration = async (apiName) => {
    if (!window.confirm(`Are you sure you want to delete the configuration for ${apiName}?`)) {
      return;
    }

    try {
      await api.delete(`/api/v1/settings/integrations/${apiName}`, {
        headers: {
          'X-Tenant-ID': tenantId || 'tenant-123'
        }
      });

      toast.success('🗑️ API configuration deleted successfully!');
      fetchAPIConfigurations();
    } catch (error) {
      console.error('Error deleting API configuration:', error);
      toast.error('❌ Failed to delete configuration: ' + (error.response?.data?.detail || error.message));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-green-100 dark:bg-green-900', text: 'text-green-800 dark:text-green-200', icon: '✅', label: 'ACTIVE' },
      inactive: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-800 dark:text-gray-200', icon: '⚪', label: 'INACTIVE' },
      error: { bg: 'bg-red-100 dark:bg-red-900', text: 'text-red-800 dark:text-red-200', icon: '❌', label: 'ERROR' },
      not_configured: { bg: 'bg-yellow-100 dark:bg-yellow-900', text: 'text-yellow-800 dark:text-yellow-200', icon: '⚠️', label: 'NOT SET' }
    };

    const config = statusConfig[status] || statusConfig.not_configured;
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.icon} {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">⚡ API Configuration</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage all third-party API integrations
          </p>
        </div>
        
        <div className="relative">
          <select
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer font-medium"
            onChange={(e) => {
              if (e.target.value) {
                openAddModal(e.target.value);
                e.target.value = '';
              }
            }}
            defaultValue=""
          >
            <option value="" disabled>+ Add API</option>
            {Object.entries(API_TEMPLATES).map(([key, template]) => (
              <option key={key} value={key}>
                {template.icon} {template.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* API Configurations Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  API Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Endpoint
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {apiConfigs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    No API configurations yet. Click &quot;+ Add API&quot; to get started.
                  </td>
                </tr>
              ) : (
                apiConfigs.map((config) => (
                  <tr key={config.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">
                          {API_TEMPLATES[config.api_name]?.icon || '🔌'}
                        </span>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {getDisplayName(config)}
                          </div>
                          {config.health?.last_sync && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Last sync: {new Date(config.health.last_sync).toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-xs uppercase text-gray-600 dark:text-gray-400">
                        {config.api_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(config.status)}
                        {/* Veeqo sync status badge */}
                        {config.api_name === 'veeqo' && syncStatus && (
                          <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded-full">
                            📊 {syncStatus.current_order_count || 0} orders
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {config.api_name === 'veeqo' ? (
                        <div className="space-y-1">
                          <div className="text-xs">📥 Order Sync</div>
                          <div className="text-xs text-gray-400">/api/v1/veeqo/sync-all</div>
                        </div>
                      ) : (
                        config.endpoint || '-'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <button
                        onClick={() => openEditModal(config.api_name)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Configure"
                      >
                        🔧
                      </button>
                      <button
                        onClick={() => handleTestConnection(config.api_name)}
                        disabled={testingConnection === config.api_name}
                        className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                        title="Test Connection"
                      >
                        {testingConnection === config.api_name ? '⏳' : '📊'}
                      </button>
                      {/* Veeqo Sync Button */}
                      {config.api_name === 'veeqo' && (
                        <button
                          onClick={handleOpenSyncModal}
                          className="text-cyan-600 hover:text-cyan-900 dark:text-cyan-400 dark:hover:text-cyan-300"
                          title="Sync Veeqo Orders"
                        >
                          🔄
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteConfiguration(config.api_name)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete"
                      >
                        ❌
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Test Result Alert */}
      {testResult && (
        <div className={`p-4 rounded-lg ${testResult.status === 'success' ? 'bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700'}`}>
          <div className="flex items-start">
            <span className="text-2xl mr-3">
              {testResult.status === 'success' ? '✅' : '❌'}
            </span>
            <div>
              <h4 className={`font-medium ${testResult.status === 'success' ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                {testResult.status === 'success' ? 'Connection Successful' : 'Connection Failed'}
              </h4>
              <p className={`text-sm mt-1 ${testResult.status === 'success' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                {testResult.message}
              </p>
              {testResult.details && (
                <pre className="text-xs mt-2 bg-white dark:bg-gray-800 p-2 rounded">
                  {JSON.stringify(testResult.details, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Configuration Modal */}
      {showModal && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {API_TEMPLATES[selectedTemplate]?.icon} Configure {API_TEMPLATES[selectedTemplate]?.name}
                  {selectedEnvironment && formData.environment && (
                    <span className="ml-2 text-sm font-normal text-blue-600 dark:text-blue-400">
                      ({selectedEnvironment.charAt(0).toUpperCase() + selectedEnvironment.slice(1)})
                    </span>
                  )}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {API_TEMPLATES[selectedTemplate]?.fields.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  
                  {field.type === 'select' ? (
                    <select
                      value={formData[field.name] || ''}
                      onChange={(e) => {
                        // Special handling for environment field
                        if (field.name === 'environment') {
                          handleEnvironmentChange(e.target.value);
                        } else {
                          setFormData({ ...formData, [field.name]: e.target.value });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      {field.options.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type}
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder={field.label}
                    />
                  )}
                  
                  {field.hint && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      ℹ️ {field.hint}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveConfiguration}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && successDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 transform animate-slideUp">
            {/* Success Header */}
            <div className="p-8 text-center border-b border-gray-700 bg-gradient-to-b from-gray-800 to-gray-900">
              <div className="mx-auto w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mb-5 animate-scaleIn shadow-2xl shadow-green-500/30">
                <svg className="w-14 h-14 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 className="text-3xl font-bold text-white mb-3 tracking-tight">
                ✓ Configuration Saved Successfully
              </h3>
              <p className="text-gray-400 text-base font-medium">
                Your API credentials have been securely encrypted and stored
              </p>
            </div>

            {/* Success Details */}
            <div className="p-6 space-y-3">
              {/* API Info */}
              <div className="flex items-center justify-between p-4 bg-gray-800 bg-opacity-50 rounded-xl border border-gray-700 hover:border-gray-600 transition-colors">
                <div className="flex items-center space-x-3">
                  <span className="text-3xl">{successDetails.icon}</span>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">API</p>
                    <p className="text-white font-bold text-lg">{successDetails.apiName}</p>
                  </div>
                </div>
              </div>

              {/* Environment */}
              <div className="flex items-center justify-between p-4 bg-gray-800 bg-opacity-50 rounded-xl border border-gray-700 hover:border-gray-600 transition-colors">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Environment</p>
                  <p className="text-white font-bold text-lg capitalize">{successDetails.environment}</p>
                </div>
                <span className={`px-4 py-1.5 ${successDetails.environment === 'production' ? 'bg-blue-500 text-blue-100 border-blue-400' : 'bg-green-500 text-green-100 border-green-400'} bg-opacity-20 text-xs font-bold rounded-full border`}>
                  {successDetails.environment.toUpperCase()}
                </span>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between p-4 bg-gray-800 bg-opacity-50 rounded-xl border border-gray-700 hover:border-gray-600 transition-colors">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Status</p>
                  <p className="text-white font-bold text-lg">Active</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-lg shadow-green-500/50"></span>
                  <span className="text-green-400 text-sm font-bold">✓ ACTIVE</span>
                </div>
              </div>

              {/* Seller ID */}
              {successDetails.sellerId && successDetails.sellerId !== 'Not provided' && (
                <div className="p-4 bg-gray-800 bg-opacity-50 rounded-xl border border-gray-700 hover:border-gray-600 transition-colors">
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">Seller ID</p>
                  <p className="text-white font-mono text-sm bg-gray-900 px-3 py-2 rounded border border-gray-700">{successDetails.sellerId}</p>
                </div>
              )}

              {/* Region & Marketplace */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 bg-gray-800 bg-opacity-50 rounded-xl border border-gray-700 hover:border-gray-600 transition-colors">
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">Region</p>
                  <p className="text-white font-bold uppercase">{successDetails.region}</p>
                </div>
                <div className="p-4 bg-gray-800 bg-opacity-50 rounded-xl border border-gray-700 hover:border-gray-600 transition-colors">
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">Marketplace</p>
                  <p className="text-white font-mono text-xs">{successDetails.marketplace}</p>
                </div>
              </div>

              {/* Timestamp */}
              <div className="p-4 bg-gray-800 bg-opacity-50 rounded-xl border border-gray-700">
                <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-1">Saved At</p>
                <p className="text-green-400 font-medium text-sm">{successDetails.timestamp}</p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-700">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="w-full px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold text-lg rounded-xl transition-all duration-200 transform hover:scale-105 shadow-xl hover:shadow-2xl"
              >
                Done ✓
              </button>
              <div className="flex items-center justify-center space-x-2 mt-4">
                <div className="w-2 h-2 bg-gray-600 rounded-full animate-pulse"></div>
                <p className="text-center text-gray-400 text-xs font-medium">
                  Auto-closing in 3 seconds
                </p>
                <div className="w-2 h-2 bg-gray-600 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Veeqo Sync Modal - Using Shared Component */}
      <VeeqoSyncModal
        isOpen={showSyncModal}
        onClose={() => {
          setShowSyncModal(false);
          setSyncProgress(null);
        }}
        onSyncStarted={() => fetchSyncStatus()}
      />
    </div>
  );
};

export default APIConfiguration;
